import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listMedia,
  createMedia,
  updateMedia,
  deleteMedia,
  mediaKindFromMime,
  MEDIA_KIND_META,
  type ContentMedia,
  type MediaKind,
  type ContentScene,
} from '@/lib/contentStudio';
import { uploadLibraryFile } from '@/hooks/useUpload';

interface Props {
  ownerId: number;
  projectId?: number;
  scenes?: ContentScene[];
}

type KindFilter = 'all' | MediaKind;

const KIND_FILTERS: { value: KindFilter; label: string; icon: string }[] = [
  { value: 'all', label: 'Tout', icon: 'ri-apps-2-line' },
  { value: 'image', label: 'Images', icon: 'ri-image-line' },
  { value: 'video', label: 'Vidéos', icon: 'ri-film-line' },
  { value: 'audio', label: 'Audio', icon: 'ri-music-2-line' },
  { value: 'other', label: 'Fichiers', icon: 'ri-file-line' },
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Bibliothèque média du studio : import de fichiers (images, vidéos, audio),
 * recherche par nom, filtre par type, et rattachement d'un média à une scène.
 */
export default function MediaLibrary({ ownerId, projectId, scenes }: Props) {
  const [media, setMedia] = useState<ContentMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [onlyProject, setOnlyProject] = useState(false);
  const [playId, setPlayId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setMedia(await listMedia(ownerId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadLibraryFile(file, 'content-studio/media');
        // eslint-disable-next-line no-await-in-loop
        await createMedia({
          ownerId,
          kind: mediaKindFromMime(file.type),
          url,
          name: file.name,
          meta: projectId ? { project_id: projectId, mime: file.type, size: file.size } : { mime: file.type, size: file.size },
        });
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de l'import");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const attachToScene = async (m: ContentMedia, sceneId: string) => {
    const meta = { ...(m.meta || {}) } as Record<string, unknown>;
    if (sceneId) {
      meta.scene_id = Number(sceneId);
      if (projectId) meta.project_id = projectId;
    } else {
      delete meta.scene_id;
    }
    setMedia((prev) => prev.map((x) => (x.id === m.id ? { ...x, meta: meta as ContentMedia['meta'] } : x)));
    try {
      await updateMedia(m.id, { meta: meta as ContentMedia['meta'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
      await load();
    }
  };

  const handleDelete = async (m: ContentMedia) => {
    try {
      if (playId === m.id) { audioRef.current?.pause(); setPlayId(null); }
      await deleteMedia(m.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const toggleAudio = (m: ContentMedia) => {
    if (playId === m.id) { audioRef.current?.pause(); setPlayId(null); return; }
    if (audioRef.current) {
      audioRef.current.src = m.url;
      audioRef.current.play().then(() => setPlayId(m.id)).catch(() => setError('Lecture impossible.'));
    }
  };

  const sceneLabel = (sceneId?: number): string => {
    if (!sceneId) return '';
    const s = scenes?.find((x) => x.id === sceneId);
    return s ? `Scène ${pad(s.number)}` : `Scène #${sceneId}`;
  };

  const filtered = media
    .filter((m) => (kindFilter === 'all' ? true : m.kind === kindFilter))
    .filter((m) => !onlyProject || (m.meta?.project_id === projectId))
    .filter((m) => !search.trim() || m.name.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-gallery-line text-foreground-500"></i>Bibliothèque média
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Importez vos images, vidéos et fichiers audio, puis rattachez-les aux scènes du projet.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
        >
          {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
          {uploading ? 'Import…' : 'Importer des médias'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*,audio/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Recherche + filtres */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un média…"
            className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300"
          />
        </div>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 flex-wrap">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setKindFilter(f.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                kindFilter === f.value ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={f.icon}></i>{f.label}
            </button>
          ))}
        </div>
        {projectId && (
          <label className="flex items-center gap-2 text-xs text-foreground-600 whitespace-nowrap cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyProject}
              onChange={(e) => setOnlyProject(e.target.checked)}
              className="accent-[oklch(var(--primary-500))] cursor-pointer"
            />
            Ce projet uniquement
          </label>
        )}
      </div>

      <audio ref={audioRef} className="hidden" />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-gallery-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">{media.length === 0 ? 'Bibliothèque vide' : 'Aucun résultat'}</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">
            {media.length === 0 ? 'Importez des images, vidéos ou fichiers audio pour commencer.' : 'Essayez un autre mot-clé ou un autre filtre.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((m) => {
            const kind = (m.kind as MediaKind) || 'other';
            const meta = MEDIA_KIND_META[kind] || MEDIA_KIND_META.other;
            return (
              <div key={m.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
                <div className="relative h-32 bg-background-100 flex items-center justify-center overflow-hidden">
                  {kind === 'image' && (
                    <img src={m.url} alt={m.name} className="w-full h-full object-cover" />
                  )}
                  {kind === 'video' && (
                    <video src={m.url} className="w-full h-full object-cover" muted preload="metadata" />
                  )}
                  {kind === 'audio' && (
                    <button
                      type="button"
                      onClick={() => toggleAudio(m)}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer ${playId === m.id ? 'bg-accent-500 text-background-50' : 'bg-background-50 text-foreground-600 hover:bg-background-200/70'}`}
                    >
                      <i className={playId === m.id ? 'ri-pause-line text-lg' : 'ri-play-line text-lg'}></i>
                    </button>
                  )}
                  {kind === 'other' && <i className="ri-file-line text-3xl text-foreground-300"></i>}
                  <span className={`absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${meta.className}`}>
                    <i className={meta.icon}></i>{meta.label}
                  </span>
                </div>
                <div className="p-2.5 flex-1 flex flex-col">
                  <p className="text-xs font-medium text-foreground-800 truncate" title={m.name}>{m.name}</p>
                  <p className="text-[10px] text-foreground-400 mt-0.5">{formatBytes(m.meta?.size)}</p>

                  {scenes && scenes.length > 0 && (
                    <div className="mt-2">
                      <div className="relative">
                        <select
                          value={m.meta?.scene_id ? String(m.meta.scene_id) : ''}
                          onChange={(e) => attachToScene(m, e.target.value)}
                          className="w-full appearance-none px-2 py-1.5 pr-7 bg-background-50 border border-background-200/70 rounded-md text-[11px] text-foreground-700 focus:outline-none focus:border-accent-300 cursor-pointer"
                        >
                          <option value="">Rattacher à une scène…</option>
                          {scenes.map((s) => <option key={s.id} value={s.id}>Scène {pad(s.number)}</option>)}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs pointer-events-none"></i>
                      </div>
                      {m.meta?.scene_id ? (
                        <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-medium">
                          <i className="ri-link"></i>{sceneLabel(m.meta.scene_id)}
                        </span>
                      ) : null}
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-background-200/70">
                    <a
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md border border-background-200/70 text-[11px] font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-external-link-line"></i>Voir
                    </a>
                    <button
                      type="button"
                      onClick={() => handleDelete(m)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      title="Supprimer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}